import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash.debounce';

import elasticQueryClient from '@src/repositories/ElasticQueryClient';
import type {
  ElasticQueryFilters,
  ElasticQueryParams,
} from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';

import { buildSearchQuery } from './buildSearchQuery';
import type {
  ElasticPaginatedSelectOption,
  UseElasticPaginatedSelectParams,
  UseElasticPaginatedSelectResult,
} from './ElasticPaginatedSelect.interfaces';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_DEBOUNCE_MS = 500;
const EMPTY_DIVISIONS: string[] = [];
const EMPTY_FILTERS: ElasticQueryFilters = {};

/**
 * Ensures selected options are present in the list. Returns the same reference
 * when no change is needed so callers can safely bail out of state updates.
 */
const mergeSelectedOptions = (
  options: ElasticPaginatedSelectOption[],
  selectedValues: string[],
  cache: Map<string, ElasticPaginatedSelectOption>,
): ElasticPaginatedSelectOption[] => {
  const optionValues = new Set(options.map(option => String(option.value)));
  const missingOptions = selectedValues
    .filter(selectedValue => !optionValues.has(selectedValue))
    .map(selectedValue => cache.get(selectedValue))
    .filter((option): option is ElasticPaginatedSelectOption => option !== undefined);

  return missingOptions.length > 0 ? [...missingOptions, ...options] : options;
};

const getSelectedValues = (
  value: string | string[] | undefined,
  isSingleMode: boolean,
): string[] => {
  if (isSingleMode) return typeof value === 'string' && value ? [value] : [];
  return Array.isArray(value) ? value.filter(Boolean) : [];
};

/**
 * Headless logic for an async, paginated, debounced-search select backed by the
 * Elastic query client (repository). All mutable bookkeeping lives in refs so
 * unstable props never retrigger fetches, and an incrementing request id drops
 * stale responses.
 */
export function useElasticPaginatedSelect<TSource = Record<string, unknown>>(
  params: UseElasticPaginatedSelectParams<TSource>,
): UseElasticPaginatedSelectResult {
  const {
    entity,
    organizationId,
    divisionIds = EMPTY_DIVISIONS,
    includeEmptyDivisionScope = false,
    baseFilters = EMPTY_FILTERS,
    sort,
    fields,
    pageSize = DEFAULT_PAGE_SIZE,
    searchKey = 'firstName',
    value,
    mode,
    enabled = true,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minSearchLength = 0,
  } = params;

  const isSingleMode = !mode || mode === undefined;

  const [options, setOptions] = useState<ElasticPaginatedSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const fromRef = useRef(0);
  const hasMoreRef = useRef(true);
  const totalRef = useRef(0);
  const isFetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const resolvedValuesRef = useRef<Set<string>>(new Set());

  const valueRef = useRef<string | string[] | undefined>(value);
  const selectedOptionsRef = useRef<Map<string, ElasticPaginatedSelectOption>>(new Map());
  const latestRef = useRef(params);
  latestRef.current = params;

  const getSelectedOnlyOptions = useCallback(() => {
    return mergeSelectedOptions(
      [],
      getSelectedValues(valueRef.current, isSingleMode),
      selectedOptionsRef.current,
    );
  }, [isSingleMode]);

  const meetsMinSearchLength = useCallback((searchText: string) => {
    const min = latestRef.current.minSearchLength ?? 0;
    return min <= 0 || searchText.trim().length >= min;
  }, []);

  useEffect(() => {
    valueRef.current = value;
    const selectedValues = getSelectedValues(value, isSingleMode);
    const selectedValueSet = new Set(selectedValues);

    selectedOptionsRef.current.forEach((_option, optionValue) => {
      if (!selectedValueSet.has(optionValue)) selectedOptionsRef.current.delete(optionValue);
    });
    options.forEach(option => {
      const optionValue = String(option.value);
      if (selectedValueSet.has(optionValue)) {
        selectedOptionsRef.current.set(optionValue, option);
      }
    });
  }, [isSingleMode, options, value]);

  const canFetch = enabled && Boolean(organizationId);

  const serializedParams = useMemo(
    () =>
      JSON.stringify({
        entity,
        organizationId,
        divisionIds,
        includeEmptyDivisionScope,
        baseFilters,
        sort,
        fields,
        pageSize,
        searchKey,
        enabled,
        minSearchLength,
      }),
    [
      baseFilters,
      divisionIds,
      enabled,
      entity,
      fields,
      includeEmptyDivisionScope,
      minSearchLength,
      organizationId,
      pageSize,
      searchKey,
      sort,
    ],
  );

  const buildRequestParams = useCallback(
    (from: number, searchText: string): ElasticQueryParams => {
      const current = latestRef.current;
      const trimmed = searchText.trim();
      const filters: ElasticQueryFilters = { ...(current.baseFilters ?? EMPTY_FILTERS) };

      if (trimmed) {
        const searchQuery = buildSearchQuery(current.searchKey ?? 'firstName', trimmed);
        filters.textSearch = filters.textSearch
          ? `(${searchQuery}) AND (${filters.textSearch})`
          : searchQuery;
      }

      return {
        entity: current.entity,
        organizationId: current.organizationId,
        divisionIds: current.divisionIds ?? EMPTY_DIVISIONS,
        ...(current.includeEmptyDivisionScope ? { includeEmptyDivisionScope: true } : {}),
        filters,
        pagination: { from, size: current.pageSize ?? DEFAULT_PAGE_SIZE },
        ...(current.sort ? { sort: current.sort } : {}),
        ...(current.fields ? { fields: current.fields } : {}),
      };
    },
    [],
  );

  const registerSelection = useCallback((option: ElasticPaginatedSelectOption) => {
    const optionValue = String(option.value);
    selectedOptionsRef.current.set(optionValue, option);
    setOptions(prevOptions =>
      prevOptions.some(currentOption => String(currentOption.value) === optionValue)
        ? prevOptions
        : [option, ...prevOptions],
    );
  }, []);

  const fetchOptions = useCallback(
    async (searchText: string, append: boolean) => {
      if (
        latestRef.current.enabled === false ||
        !latestRef.current.organizationId ||
        isFetchingRef.current
      )
        return;
      if (append && !hasMoreRef.current) return;

      if (!append && !meetsMinSearchLength(searchText)) {
        hasMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
        setOptions(getSelectedOnlyOptions());
        return;
      }

      const from = append ? fromRef.current : 0;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      isFetchingRef.current = true;

      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(false);

      try {
        const client = latestRef.current.queryClient ?? elasticQueryClient;
        const response = await client.query(buildRequestParams(from, searchText));

        if (requestId !== requestIdRef.current) return;

        const parsed = response
          ? latestRef.current.parseData(response as ElasticSearchResponse<TSource>)
          : [];
        const nextOptions = Array.isArray(parsed) ? parsed : [];
        totalRef.current = response?.hits?.total?.value ?? 0;

        setOptions(prevOptions => {
          const base = append ? [...prevOptions, ...nextOptions] : nextOptions;
          const selectedValues = getSelectedValues(valueRef.current, isSingleMode);
          const merged = mergeSelectedOptions(base, selectedValues, selectedOptionsRef.current);
          hasMoreRef.current = totalRef.current > from + nextOptions.length;
          return merged;
        });

        fromRef.current = from + (latestRef.current.pageSize ?? DEFAULT_PAGE_SIZE);
      } catch {
        setError(true);
      } finally {
        if (requestId === requestIdRef.current) {
          isFetchingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [buildRequestParams, getSelectedOnlyOptions, isSingleMode, meetsMinSearchLength],
  );

  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  const debouncedFetch = useMemo(
    () => debounce((searchText: string, append: boolean) => fetchOptionsRef.current(searchText, append), debounceMs),
    [debounceMs],
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  useEffect(() => {
    debouncedFetch.cancel();
    requestIdRef.current += 1;
    isFetchingRef.current = false;
    fromRef.current = 0;
    totalRef.current = 0;
    resolvedValuesRef.current = new Set();

    if (!canFetch) {
      hasMoreRef.current = false;
      setOptions([]);
      setLoading(false);
      setLoadingMore(false);
      setError(false);
      return;
    }

    if (minSearchLength > 0) {
      hasMoreRef.current = false;
      setOptions(getSelectedOnlyOptions());
      setLoading(false);
      setLoadingMore(false);
      setError(false);
      return;
    }

    hasMoreRef.current = true;
    fetchOptionsRef.current('', false);
  }, [canFetch, debouncedFetch, getSelectedOnlyOptions, minSearchLength, serializedParams]);

  // Resolve all preselected values missing from the current page in one terms query.
  useEffect(() => {
    if (!canFetch || isFetchingRef.current) return;

    const optionValues = new Set(options.map(option => String(option.value)));
    const missingValues = getSelectedValues(value, isSingleMode).filter(
      selectedValue =>
        !optionValues.has(selectedValue) && !resolvedValuesRef.current.has(selectedValue),
    );
    if (missingValues.length === 0) return;

    missingValues.forEach(missingValue => resolvedValuesRef.current.add(missingValue));
    let cancelled = false;

    (async () => {
      const current = latestRef.current;
      const client = current.queryClient ?? elasticQueryClient;
      try {
        const response = await client.query({
          entity: current.entity,
          organizationId: current.organizationId,
          divisionIds: current.divisionIds ?? EMPTY_DIVISIONS,
          ...(current.includeEmptyDivisionScope ? { includeEmptyDivisionScope: true } : {}),
          filters: { ...(current.baseFilters ?? EMPTY_FILTERS), terms: { id: missingValues } },
          pagination: { from: 0, size: missingValues.length },
        });

        if (cancelled || !response) return;

        const parsed = current.parseData(response as ElasticSearchResponse<TSource>);
        const resolvedOptions = Array.isArray(parsed) ? parsed : [];
        resolvedOptions.forEach(option => {
          selectedOptionsRef.current.set(String(option.value), option);
        });
        setOptions(prevOptions =>
          mergeSelectedOptions(
            prevOptions,
            getSelectedValues(valueRef.current, isSingleMode),
            selectedOptionsRef.current,
          ),
        );
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canFetch, isSingleMode, options, serializedParams, value]);

  const onSearch = useCallback(
    (searchText: string) => {
      if (!meetsMinSearchLength(searchText)) {
        debouncedFetch.cancel();
        requestIdRef.current += 1;
        isFetchingRef.current = false;
        hasMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
        setOptions(getSelectedOnlyOptions());
        return;
      }
      debouncedFetch(searchText, false);
    },
    [debouncedFetch, getSelectedOnlyOptions, meetsMinSearchLength],
  );

  const onLoadMore = useCallback(
    (searchText: string) => {
      if (!meetsMinSearchLength(searchText)) return;
      debouncedFetch(searchText, true);
    },
    [debouncedFetch, meetsMinSearchLength],
  );

  const reset = useCallback(() => {
    debouncedFetch.cancel();
    requestIdRef.current += 1;
    isFetchingRef.current = false;
    fromRef.current = 0;
    resolvedValuesRef.current = new Set();

    if (minSearchLength > 0) {
      hasMoreRef.current = false;
      setLoading(false);
      setLoadingMore(false);
      setOptions(getSelectedOnlyOptions());
      return;
    }

    hasMoreRef.current = true;
    fetchOptionsRef.current('', false);
  }, [debouncedFetch, getSelectedOnlyOptions, minSearchLength]);

  return {
    options,
    loading,
    loadingMore,
    error,
    hasMore: hasMoreRef.current,
    onSearch,
    onLoadMore,
    reset,
    registerSelection,
  };
}
